import { useState } from 'react';

export type SortDirection = 'asc' | 'desc' | null;

export interface EditableProps {
  columnKey: string;
  rowId: string;
  isEditing: boolean;
}

export interface UseTableProps {
  onSort?: (key: string, direction: SortDirection) => void;
  onEditSubmit?: (
    rowId: string,
    columnKey: string,
    newValue: string | number
  ) => void;
}

export function useTable({ onSort, onEditSubmit }: UseTableProps = {}) {
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>(null);
  const [editing, setEditing] = useState<EditableProps>({
    isEditing: false,
    columnKey: '',
    rowId: '',
  });
  const [isSubmittingEdit, setIsSubmittingEdit] = useState(false);

  const handleSort = (columnKey: string) => {
    let newDirection: SortDirection = 'asc';

    if (sortKey === columnKey) {
      if (sortDirection === 'asc') {
        newDirection = 'desc';
      } else if (sortDirection === 'desc') {
        newDirection = null;
      } else {
        newDirection = 'asc';
      }
    }
    setSortKey(newDirection ? columnKey : null);
    setSortDirection(newDirection);
    onSort?.(columnKey, newDirection);
  };

  const handleEditContinue = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmittingEdit(true);
    onEditSubmit?.(
      editing.rowId,
      editing.columnKey,
      (e.currentTarget.elements.namedItem('editTableInput') as HTMLInputElement)
        .value
    );
  };

  const handleEditClick = (columnKey: string, rowId: string) => {
    setEditing({ isEditing: true, columnKey, rowId });
    setIsSubmittingEdit(false);
  };

  const handleEditDone = () => {
    setEditing({ isEditing: false, columnKey: '', rowId: '' });
    setIsSubmittingEdit(false);
  };

  return {
    sortKey,
    sortDirection,
    editing,
    isSubmittingEdit,
    handleSort,
    handleEditContinue,
    handleEditClick,
    handleEditDone,
  };
}
